import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import { ArrowLeft, Camera, Eye, EyeSlash } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const avatarUrl = (p) => (p ? `${BACKEND_URL}/api/files/${p}?w=240` : null);

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user.name, email: user.email, password: "", password2: "", avatar: user.avatar || "" });
  const [showPwd, setShowPwd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, avatar: r.data.path }));
      toast.success("Аватар завантажено");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Заповніть імʼя та email");
    if (form.password && form.password.length < 6) return toast.error("Пароль має містити щонайменше 6 символів");
    if (form.password && form.password !== form.password2) return toast.error("Паролі не співпадають");
    setSaving(true);
    try {
      const r = await api.put("/auth/profile", { name: form.name, email: form.email, password: form.password, avatar: form.avatar });
      setUser(r.data);
      toast.success("Профіль оновлено");
      setForm((f) => ({ ...f, password: "", password2: "" }));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground mb-3 hover:text-foreground" data-testid="back-btn">
        <ArrowLeft size={16} /> Назад
      </button>
      <PageHeader title="Мій профіль" subtitle="Редагування власних даних" />

      <Card className="p-6 space-y-5" data-testid="profile-card">
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-secondary flex items-center justify-center border border-border">
              {form.avatar
                ? <img src={avatarUrl(form.avatar)} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar" />
                : <span className="font-head font-black text-3xl text-muted-foreground">{form.name?.[0] || "?"}</span>}
            </div>
            <label className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center cursor-pointer shadow-md hover:opacity-90" data-testid="avatar-upload-label">
              <Camera size={18} />
              <input type="file" accept="image/*" hidden disabled={uploading}
                onChange={(e) => { uploadAvatar(e.target.files[0]); e.target.value = ""; }} data-testid="avatar-input" />
            </label>
          </div>
          <div>
            <div className="font-head font-bold text-lg">{form.name}</div>
            <div className="text-sm text-muted-foreground">{ROLE_LABELS[user.role]}</div>
            {uploading && <div className="text-xs text-muted-foreground mt-1">Завантаження...</div>}
          </div>
        </div>

        <div><Label>Імʼя *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="profile-name" /></div>
        <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="profile-email" /></div>
        <div><Label>Новий пароль (не обовʼязково)</Label>
          <div className="relative mt-1.5">
            <Input type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pr-10" data-testid="profile-password" />
            <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="profile-toggle-password">
              {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {form.password && form.password.length < 6 && <p className="text-xs text-destructive mt-1.5">Мінімум 6 символів</p>}
        </div>
        <div><Label>Повторіть пароль</Label>
          <Input type={showPwd ? "text" : "password"} value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} className="mt-1.5" data-testid="profile-password2" />
          {form.password2 && form.password !== form.password2 && <p className="text-xs text-destructive mt-1.5">Паролі не співпадають</p>}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-profile-btn">{saving ? "Збереження..." : "Зберегти зміни"}</Button>
        </div>
      </Card>
    </div>
  );
}
